import { createContext, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type {
  ConfirmationContextType,
  ConfirmationOptions,
  ConfirmationProviderProps,
} from './types';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';

const ConfirmationContext = createContext<ConfirmationContextType>(null!);

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);

  if (!context) {
    throw new Error(
      'useConfirmation must be used within a <ConfirmationProvider />',
    );
  }

  return context;
};

export const ConfirmationProvider = ({
  children,
}: ConfirmationProviderProps) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<ConfirmationOptions | null>(null);

  const confirm = useCallback((options: ConfirmationOptions) => {
    setOptions(options);
  }, []);

  const close = useCallback(() => {
    setOptions(null);
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!options) {
      return;
    }

    const { onConfirm, onSuccess, onError } = options;

    try {
      await onConfirm();
      if (onSuccess) {
        onSuccess();
      }
      close();
    } catch (error) {
      if (onError) {
        onError(error);
      }
    }
  }, [options, close]);

  const handleCancel = useCallback(() => {
    options?.onCancel?.();
    close();
  }, [options, close]);
  if (!options) {
    return (
      <ConfirmationContext.Provider value={{ confirm }}>
        {children}
      </ConfirmationContext.Provider>
    );
  }

  const {
    title = t('Confirmation'),
    description = t('Are you sure?'),
    children: customChildren,
    confirmLabel = t('Confirm'),
    cancelLabel = t('Close'),
    confirmVariant = 'default',
    confirmIcon,
    cancelIcon,
    customButtons,
    showCancel = true,
    contentClassName,
    headerClassName,
    footerClassName,
  } = options;

  const hasCustomButtons = customButtons && customButtons.length > 0;

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog
        open={!!options}
        onOpenChange={(open) => {
          if (!open) {
            handleCancel();
          }
        }}
      >
        <AlertDialogContent className={contentClassName}>
          <AlertDialogHeader className={headerClassName}>
            <AlertDialogTitle>{title}</AlertDialogTitle>
            {customChildren ? (
              <>
                <AlertDialogDescription />
                <div>{customChildren}</div>
              </>
            ) : (
              <AlertDialogDescription>{description}</AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className={footerClassName}>
            {hasCustomButtons ? (
              <>
                {showCancel && (
                  <AlertDialogCancel onClick={handleCancel}>
                    {cancelIcon && <span className="mr-2">{cancelIcon}</span>}
                    {cancelLabel}
                  </AlertDialogCancel>
                )}
                {customButtons.map((button, index) => {
                  const onClick = async () => {
                    const { onError } = options;

                    try {
                      await button.onClick();
                      close();
                    } catch (error) {
                      if (onError) {
                        onError(error);
                      }
                    }
                  };

                  return (
                    <Button
                      key={index}
                      variant={button.variant || 'default'}
                      onClick={onClick}
                      disabled={button.disabled}
                      className={button.className}
                    >
                      {button.icon && (
                        <span className="mr-2">{button.icon}</span>
                      )}
                      {button.label}
                    </Button>
                  );
                })}
              </>
            ) : (
              <>
                {showCancel && (
                  <AlertDialogCancel onClick={handleCancel}>
                    {cancelIcon && <span className="mr-2">{cancelIcon}</span>}
                    {cancelLabel}
                  </AlertDialogCancel>
                )}
                <AlertDialogAction
                  variant={confirmVariant}
                  onClick={handleConfirm}
                >
                  {confirmIcon && <span className="mr-2">{confirmIcon}</span>}
                  {confirmLabel}
                </AlertDialogAction>
              </>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmationContext.Provider>
  );
};
